
import { supabase } from '@/integrations/supabase/client';

export interface InvitationResult {
  clientId: string;
  success: boolean;
  email?: string;
  error?: string;
  expiresAt?: string;
}

export interface InvitationResponse {
  success: boolean;
  results: InvitationResult[];
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

/**
 * Send account setup invitation to a single client
 */
export const sendAccountSetupInvitation = async (clientId: string): Promise<InvitationResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('send-account-setup-invitation', {
      body: { clientId }
    });

    if (error) {
      console.error('Error sending invitation:', error);
      return {
        success: false,
        results: [{ clientId, success: false, error: error.message }],
        summary: { total: 1, successful: 0, failed: 1 }
      };
    }

    return data;
  } catch (error: any) {
    console.error('Error invoking invitation function:', error);
    return {
      success: false,
      results: [{ clientId, success: false, error: error.message }],
      summary: { total: 1, successful: 0, failed: 1 }
    };
  }
};

/**
 * Send account setup invitations to multiple clients
 */
export const sendBulkAccountSetupInvitations = async (clientIds: string[]): Promise<InvitationResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke('send-account-setup-invitation', {
      body: { clientIds }
    });

    if (error) {
      console.error('Error sending bulk invitations:', error);
      return {
        success: false,
        results: clientIds.map(id => ({ clientId: id, success: false, error: error.message })),
        summary: { total: clientIds.length, successful: 0, failed: clientIds.length }
      };
    }

    return data;
  } catch (error: any) {
    console.error('Error invoking bulk invitation function:', error);
    return {
      success: false,
      results: clientIds.map(id => ({ clientId: id, success: false, error: error.message })),
      summary: { total: clientIds.length, successful: 0, failed: clientIds.length }
    };
  }
};

/**
 * Check the authentication status of a client
 */
export const getClientAuthStatus = (client: any): 'registered' | 'wave_import' | 'invitation_sent' | 'invitation_expired' => {
  // If account setup is completed, they're registered
  if (client.account_setup_completed_at) {
    return 'registered';
  }

  // If invitation was sent and not expired
  if (client.invitation_sent_at) {
    if (client.invitation_expires_at && new Date(client.invitation_expires_at) > new Date()) {
      return 'invitation_sent';
    } else if (client.invitation_expires_at) {
      return 'invitation_expired';
    }
  }

  // If they have a Wave customer ID, they're a Wave import
  if (client.wave_customer_id) {
    return 'wave_import';
  }

  // Default for regular registrations
  return 'registered';
};

/**
 * Get display text for authentication status
 */
export const getAuthStatusDisplay = (status: ReturnType<typeof getClientAuthStatus>): { text: string; color: string } => {
  switch (status) {
    case 'registered':
      return { text: 'Registered', color: 'text-green-600' };
    case 'wave_import':
      return { text: 'Needs Setup', color: 'text-orange-600' };
    case 'invitation_sent':
      return { text: 'Invitation Sent', color: 'text-blue-600' };
    case 'invitation_expired':
      return { text: 'Invitation Expired', color: 'text-red-600' };
    default:
      return { text: 'Unknown', color: 'text-gray-600' };
  }
};
